import {Component, inject, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    DxButtonComponent,
    DxDataGridComponent,
    DxFormComponent,
    DxTemplateDirective,
} from "devextreme-angular";
import {
    DxiColumnComponent, DxiItemComponent, DxoColumnChooserComponent, DxoExportComponent, DxoFilterRowComponent,
    DxoHeaderFilterComponent, DxoLoadPanelComponent, DxoPagerComponent,
    DxoPagingComponent, DxoPositionComponent,
    DxoScrollingComponent, DxoSearchComponent, DxoSearchPanelComponent, DxoSelectionComponent, DxoSortingComponent,
    DxoToolbarComponent
} from "devextreme-angular/ui/nested";
import {ContactStatusComponent, FormPopupComponent} from "../../../components";
import {
    PunchInStatus,
    PunchOutStatus,
    CreateEmployeeCommand,
    EmployeeGridViewDto,
    EmployeesClient,
    PositionStatus,
    ShiftType, AuthService, PermissionType, AccessType, RegularPayRateCode, PayrollCompanyCode, JobTitle, NestleStatus
} from "@services";
import {DxDataGridTypes} from "devextreme-angular/ui/data-grid";
import {Router} from "@angular/router";
import {AddEmployeeFormComponent} from "./add-employee-form/add-employee-form.component";
import notify from "devextreme/ui/notify";
import {UserAvatarEmailComponent} from "../../../components/utils/user-avatar-email/user-avatar-email.component";
import {FilterPanelComponent} from "../../../components/library/filter-panel/filter-panel.component";
import EnumUtils from "../../../shared/enum-utils";
import {DasherizePipe} from "../../../pipes/dasherize.pipe";
import {CapitalCasePipe} from "../../../pipes/capital-case.pipe";
import {PositionStatusPipe} from "../../../pipes/position-status.pipe";
import {NestleStatusPipe} from "../../../pipes/nestle-status.pipe";
import {FilterConfig, GridPageComponent} from "../../common/grid-page.component";
import {YES_NO_OPTIONS} from "../../../shared/constants";

@Component({
    selector: 'app-employees',
    imports: [
        CommonModule,
        DxDataGridComponent,
        DxiColumnComponent,
        DxoHeaderFilterComponent,
        ContactStatusComponent,
        DxButtonComponent,
        DxTemplateDirective,
        DxiItemComponent,
        DxoColumnChooserComponent,
        DxoExportComponent,
        DxoLoadPanelComponent,
        DxoSearchPanelComponent,
        DxoSortingComponent,
        DxoToolbarComponent,
        FormPopupComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoFilterRowComponent,
        AddEmployeeFormComponent,
        DasherizePipe,
        DxFormComponent,
        UserAvatarEmailComponent,
        FilterPanelComponent,
        DxoPositionComponent,
        DxoSearchComponent,
        DxoSelectionComponent,
        CapitalCasePipe,
        PositionStatusPipe,
        NestleStatusPipe,
        DxoScrollingComponent,
    ],
    providers: [PositionStatusPipe, NestleStatusPipe],
    templateUrl: './employees.component.html',
    styleUrl: './employees.component.scss',
})
export class EmployeesComponent extends GridPageComponent<EmployeeGridViewDto> {
    @ViewChild(AddEmployeeFormComponent, {static: false}) createNewForm: AddEmployeeFormComponent;

    private employeesClient = inject(EmployeesClient);
    private router = inject(Router);
    protected authService = inject(AuthService);
    private positionStatusPipe = inject(PositionStatusPipe);
    private nestleStatusPipe = inject(NestleStatusPipe);

    constructor() {
        super();
        this.init(EmployeeGridViewDto);
    }

    isAddEmployeePopupOpened = false;
    employeeId: string;
    dataSource = this.dataSources.employeeList()
    shiftTypeOptions = EnumUtils.getEnumOptions(ShiftType);
    statusOptions = EnumUtils.getEnumOptions(PositionStatus, (v) =>
        this.positionStatusPipe.transform(v),
    );
    clockInStatusOptions = EnumUtils.getEnumOptions(PunchInStatus);
    clockOutStatusOptions = EnumUtils.getEnumOptions(PunchOutStatus);
    regularPayRateCodeOptions = EnumUtils.getEnumOptions(RegularPayRateCode);
    payrollCompanyCodeOptions = EnumUtils.getEnumOptions(PayrollCompanyCode);
    jobTitleOptions = EnumUtils.getEnumOptions(JobTitle);
    nestleStatusOptions = EnumUtils.getEnumOptions(NestleStatus, (v) =>
        this.nestleStatusPipe.transform(v),
    );
    yesNoOptions = YES_NO_OPTIONS;

    protected override exportName: string = "Employees";

    override nameOrEmailFilter = (filterValue: any, selectedFilterOperation: string) => {
        if (!filterValue) {
            return null;
        }

        return [
            ['name', selectedFilterOperation || 'contains', filterValue],
            'or',
            ['email', selectedFilterOperation || 'contains', filterValue]
        ];
    };

    protected override filterConfig: FilterConfig[] = [
        {field: 'name', op: 'contains', customFilter: this.nameOrEmailFilter},
        {field: 'positionStatus', op: '='},
        {field: 'shiftType', op: '='},
        {field: 'punchInStatus', op: '='},
        {field: 'punchOutStatus', op: '='},
        {field: 'requests', op: '>='},
        {field: 'lastUpdated', op: '>='},
        {field: 'positionId', op: 'contains'},
        {field: 'homeCostNumberCode', op: 'contains'},
        {field: 'homeDepartmentCode', op: 'contains'},
        {field: 'payClass', op: '='},
        {field: 'emergencyContactName', op: 'contains'},
        {field: 'emergencyContactNumber', op: 'contains'},
        {field: 'medicalConditions', op: 'contains'},
        {field: 'taxId', op: 'contains'},
        {field: 'payrollCompanyCode', op: '='},
        {field: 'regularPayRateCode', op: '='},
        {field: 'payRateAmount', op: '>='},
        {field: 'annualSalary', op: '>='},
        {field: 'birthDate', op: '>='},
        {field: 'regularPayEffectiveDate', op: '>='},
        {field: 'hireDate', op: '>='},
        {field: 'paidPosition', op: 'contains'},
        {field: 'standardHours', op: '>='},
    ];


    addEmployee() {
        this.isAddEmployeePopupOpened = true;
    };

    override async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        const {data} = e;

        this.employeeId = data.id;
        await this.router.navigate([`/employee/${this.employeeId}`]);
    }

    onAddEmployeePopupSaveClick = () => {
        let formData = this.createNewForm.getData();

        let createCommand = new CreateEmployeeCommand(formData)
        this.employeesClient.createEmployee(createCommand).subscribe({
            next: async () => {
                this.isAddEmployeePopupOpened = false;
            },
            error: (errors: Array<any>) => {
                errors.forEach((errMsg => {
                    notify({
                        message: errMsg.message,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                }));
            }
        });
    };

    getClockInStatus(cellInfo: { data: EmployeeGridViewDto }) {
        return EnumUtils.getEnumKeyByValue(PunchInStatus, cellInfo.data.punchInStatus);
    }

    getClockOutStatus(cellInfo: { data: EmployeeGridViewDto }) {
        return EnumUtils.getEnumKeyByValue(PunchOutStatus, cellInfo.data.punchOutStatus);
    }

    getNestleStatusBadgeClass(status: NestleStatus) {
        return EnumUtils.getNestleStatusBadgeClass(status);
    }

    protected readonly PermissionType = PermissionType;
    protected readonly AccessType = AccessType;
}
